import { debounce } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomIcon } from '~components/index';
import InputLabel from '../InputLabel/InputLabel';
import '../inputs.scss';

interface Props extends CommonInputProps {
  value?: string | number;
  defaultValue?: string | number;
  rows?: number;
  iconName?: IconName;
  hookOnChange?: (value: string) => void;
  onInputChange?: (value: string) => void;
  debounceTime?: number;
}

const InputTextArea = ({
  name,
  label,
  placeholder,
  labelHelper,
  iconName,
  value,
  defaultValue,
  disabled = false,
  readOnly = false,
  required = false,
  design = 'col-12',
  hookError,
  hookOnChange = () => {},
  onInputChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  debounceTime = 300
}: Props) => {
  const { t } = useTranslation();
  const isControlled = value !== undefined;

  const debouncedOnInputChange = useMemo(
    () => debounce((nextValue: string) => onInputChange(nextValue), debounceTime),
    [onInputChange, debounceTime]
  );

  useEffect(() => () => debouncedOnInputChange.cancel(), [debouncedOnInputChange]);

  return (
    <div className={`customInput customInput_textArea ${design}`}>
      {label && (
        <InputLabel
          helper={labelHelper}
          name={name}
          text={label}
          required={required}
        />
      )}
      <div
        className={`customInput_container ${
          disabled ? 'customInput_disabled' : ''
        }`}
      >
        {iconName && (
          <CustomIcon
            className="customInput_icon"
            name={iconName}
            color="var(--gray)"
          />
        )}
        <textarea
          rows={1}
          id={name}
          placeholder={placeholder && t(placeholder)}
          disabled={disabled}
          readOnly={readOnly}
          value={isControlled ? String(value ?? '') : undefined}
          defaultValue={!isControlled ? defaultValue : undefined}
          onChange={(e) => {
            const nextValue = e.target.value;
            hookOnChange(nextValue);
            debouncedOnInputChange(nextValue);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </div>
      {hookError?.message && (
        <p className="customInput_error">{t(hookError?.message)}</p>
      )}
    </div>
  );
};

export default InputTextArea;
